/**
 *
 * StudentsDetail
 *
 */

import React, { memo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import { flushState, loadStudent } from './actions';
import {
  makeSelectLoading,
  makeSelectError,
  makeSelectItemData,
} from './selectors';

// antd component
import { Col, Row, Tabs, message } from 'antd';
const { TabPane } = Tabs;

// assets
//import LogoClient from 'images/logo.svg';
//import CustomIcons from 'components/CustomIcons';

// components
import Preloader from 'components/Preloader';
import CustomPageHeader from 'components/CustomPageHeader';
import CustomForm from 'components/Forms/CustomForm';
import InfoCard from 'components/InfoCard';
import AbsencesCard from 'components/Absences/AbsencesCard';

// Custom Tabs
import TabParents from 'components/Tabs/TabParents';
import TabStudentCourses from 'components/Tabs/TabStudentCourses';
import TabCourses from 'components/Tabs/TabCourses';
import RenderPopconfirm from 'components/RenderPopconfirm';

// helper for forms
import { isEqualFormValues } from 'utils/forms/forms.utils';


export function StudentsDetail({
  flushState,
  // reducer main props
  loading,
  itemData,
  loadStudent,
  // custom props
  currentId,
  update,
  updatedItem,
  // form actions and data
  form,
  generalForm,
  // parent states
  formHaveChanges,
  setFormHaveChanges,
  currentElement,
  setCurrentElement
}) {
  useInjectReducer({ key: 'studentsDetail', reducer });
  useInjectSaga({ key: 'studentsDetail', saga });

  const newKey = 'nouvel';

  const [initID, setInitID] = useState(currentId);

  // states for tabs toggle
  const [activeTab, setActiveTab] = useState('general');
  const [stayActiveTab, setStayActiveTab] = useState(false);
  // states for main data
  const [currentParents, setCurrentParents] = useState([]);
  const [currentCourses, setCurrentCourses] = useState([]);

  // Form initialization for each tab
  const { customForm, resetForm, submitForm, setFormData } = form;

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    // reset data in the general form
    resetForm();
    // refresh state for new item
    if (!currentId || currentId == newKey) {
      // Set default data for NEW Item
      setCurrentElement(false);
      setActiveTab('general');
    } else if (currentId && currentId !== newKey) {
      setCurrentElement(false);
      loadStudent(currentId);
    }

    setInitID(currentId);
  }, [currentId]);

  // Set data for opened item
  useEffect(() => {
    if (itemData || updatedItem) {
      // check if updated data the same as current data
      let initData;
      if (currentId == updatedItem?._id || currentId == newKey) {
        initData = updatedItem;
      } else {
        initData =
          itemData?._id == updatedItem?._id
            ? updatedItem || itemData
            : itemData;
      }

      setInitID(initData?._id);
      setCurrentElement(initData);

      // set parents list
      if (
        JSON.stringify(currentParents) !== JSON.stringify(initData?.parents)
      ) {
        setCurrentParents(initData?.parents);
      }

      // set courses list
      if (
        JSON.stringify(currentCourses) !== JSON.stringify(initData?.courses)
      ) {
        setCurrentCourses(initData?.courses);
      }

      // reset data in the general form
      setFormData(initData);
    }
  }, [itemData, updatedItem]);

  // Clearing the state after unmounting a component
  useEffect(() => {
    return () => {
      // Anything in here is fired on component unmount.
      flushState();
    };
  }, []);

  // General Form action
  const onSubmitForm = data => {
    const updatedData = {
      ...currentElement,
      ...data,
    };
    update(updatedData);
    checkFromChanges(false)
    message.success('Données enregistrées avec succès');
  };

  const onFormChange = useCallback((changedFields, allFields) => {
    checkFromChanges(true);
  }, []);

  const onFormValuesChange = useCallback((changedValues, allValues) => {
    const formVals = Object.values(allValues);
    const formHasAnyVals = formVals.some(val => val != '' && val != undefined && val != null);
    const isEqualValuesWithElement = isEqualFormValues(generalForm, currentElement, allValues);
    if (
      (!isEqualValuesWithElement && currentElement) ||
      (!isEqualValuesWithElement && formHasAnyVals && !currentElement)
    ) {
      checkFromChanges(true);
    } else {
      checkFromChanges(false);
    }
  }, [currentElement, generalForm]);

  // Set if form have any changes
  const checkFromChanges = haveChange => {
    setFormHaveChanges(haveChange);
    setStayActiveTab(haveChange);
  };

  /*
   * Сhange the way to switch tabs,
   * if there are changes in the form in the active tab
   */
  const onChangeTab = (key, event) => {
    if (formHaveChanges) {
      event.stopPropagation();
      !stayActiveTab && setActiveTab(key);
    } else {
      setActiveTab(key);
    }
  };

  /*
   * Custom tab title. Added Popconfirm window,
   * if there are changes in the form in the active tab
   */
  const renderTabTitle = useCallback(
    (tabTitle, key) => (
      <RenderPopconfirm
        showPopup={formHaveChanges}
        title={tabTitle}
        key={key}
        onCancel={() => {
          setActiveTab(key);
          setStayActiveTab(false);
          setFormHaveChanges(false);
          // update data in the general form
          setFormData();
        }}
        onConfirm={() => {
          setActiveTab(key);
          setStayActiveTab(false);
          setFormHaveChanges(false);
          submitForm();
        }}
      />
    ),
    [formHaveChanges, customForm, currentElement],
  );

  // Remove/Add (students, teachers, parents)
  const updateTableList = (array, type) => {
    if (array) {
      const updatedElem = { ...currentElement };
      if (type === 'students') {
        updatedElem.students = array;
      } else if (type === 'parents') {
        updatedElem.parents = array;
      } else if (type === 'courses') {
        updatedElem.courses = array;
      }
      update(updatedElem);
    }
  };

  /*
   * TABS Content
   */
  const tabAbsences = () => {
    return (
      <TabPane
        disabled
        tab={renderTabTitle('Absences', 'absences')}
        key="absences"
      >
        Absences tab
      </TabPane>
    );
  };
  const tabNotes = () => {
    return (
      <TabPane disabled tab={renderTabTitle('Notes', 'notes')} key="notes">
        Notes tab
      </TabPane>
    );
  };
  const tabEntretien = () => {
    return (
      <TabPane
        disabled
        tab={renderTabTitle('Entretien', 'entretien')}
        key="entretien"
      >
        Entretien tab
      </TabPane>
    );
  };
  const tabAdvanced = () => {
    return (
      <TabPane
        disabled
        tab={renderTabTitle('Gestion avançée', 'gestion_avancee')}
        key="gestion_avancee"
      >
        Gestion avançée tab
      </TabPane>
    );
  };

  // Rendering another tabs
  const otherTabs = () => {
    return (
      <>
        {
          <TabPane
            tab={renderTabTitle('Parent(s)', 'parents')}
            key="parents"
          >
            <TabParents
              parents={currentParents}
              sourceData={currentElement}
              updateSourse={object => onSubmitForm(object)}
            />
          </TabPane>
        }
        {
          <TabPane tab={renderTabTitle('Cours', 'cours')} key="cours">
            {/*  <TabStudentCourses
              courses={currentCourses}
              classInfo={currentElement?.class_info}
              onFormChange={haveChange => checkFromChanges(haveChange)}
              disabeled={!formHaveChanges}
              updateList={array => updateTableList(array, 'courses')}
            /> */}
            <TabCourses
              courses={currentCourses}
              classId={currentElement?.class_info?._id}
              classLevel={currentElement?.class_info?.level}
              classYear={currentElement?.class_info?.year}
              updateList={array => updateTableList(array, 'courses')}
              hideClass
            />
          </TabPane>
        }
        {tabAbsences()}
        {tabNotes()}
        {tabEntretien()}
        {tabAdvanced()}
      </>
    );
  };

  const contact_person =
    currentElement?.contact_person?.description &&
    `${currentElement?.contact_person?.description?.lastname} ${
      currentElement?.contact_person?.description?.firstname
    }`;

  const incumbent =
    currentElement?.class_info?.incumbent?.description &&
    `${currentElement?.class_info?.incumbent?.description?.lastname} ${
      currentElement?.class_info?.incumbent?.description?.firstname
    }`;

  // render function
  return (
    <div className="details-container">
      <Helmet>
        <title>Student</title>
        <meta name="description" content="Description of Student" />
      </Helmet>
      {!initID && loading && <Preloader />}
      {!loading && initID && (
        <>
          <CustomPageHeader
            title={
              currentElement?.description?.firstname && initID !== newKey
                ? `${currentElement?.description?.lastname} ${currentElement?.description?.firstname}`
                : 'Nouvel étudiant'
            }
            subTitle={'Étudiant'}
            subTitlePosition="top"
            //extraActions={[]}
          />
          <Row gutter={[0, 0]}>
            <Col xs={24}>
              <Tabs
                defaultActiveKey={activeTab}
                activeKey={activeTab}
                className="details-tabs"
                onTabClick={onChangeTab}
              >
                <TabPane
                  tab={renderTabTitle('Informations générales', 'general')}
                  key="general"
                  forceRender
                >
                  {initID !== newKey && (
                    <Row gutter={(15, 15)} style={{ marginBottom: '15px' }}>
                      <Col sm={24} md={24} lg={24} xl={24} xxl={12}>
                        <InfoCard title="Contact principal">
                          {contact_person}
                        </InfoCard>
                      </Col>
                      <Col sm={24} md={24} lg={24} xl={24} xxl={12}>
                        <AbsencesCard
                          title="Absences"
                          absences={currentElement?.absences || []}
                          variant="student"
                        >
                          '--'
                        </AbsencesCard>
                      </Col>
                    </Row>
                  )}
                  <Row>
                    <Col span={24}>
                      <CustomForm
                        name={`student-general-form-${initID}`}
                        form={customForm}
                        dataSource={generalForm}
                        handleSubmit={data => onSubmitForm(data)}
                        // handleChange={onFormChange}
                        handleValuesChange={onFormValuesChange}
                        labelCol={24}
                        wrapperCol={24}
                        disabeled={!formHaveChanges}
                      />
                    </Col>
                  </Row>
                  {initID !== newKey && (
                    <Row gutter={(15, 15)} style={{ marginTop: '15px' }}>
                      <Col sm={24} md={24} lg={24} xl={24}>
                        {incumbent && (
                          <InfoCard title="Titulaire">{incumbent}</InfoCard>
                        )}
                      </Col>
                    </Row>
                  )}
                </TabPane>
                {initID && initID !== newKey && otherTabs()}
              </Tabs>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}

StudentsDetail.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  update: PropTypes.func,
  loading: PropTypes.bool,
  currentId: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.number.isRequired,
    PropTypes.string.isRequired,
  ]),
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  itemData: makeSelectItemData(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
    loadStudent: currnetId => dispatch(loadStudent(currnetId)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(StudentsDetail);
